import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from '../Modal';
import OtpInput from '../OtpInput';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { prettifyErrorResponse } from 'utils';
import { setIsSupervisorMode } from 'modules/state-mgmt/app';

import { useStyles } from './styles';

export enum WorkerStatusLoginModalType {
  ADMIN = 'admin',
  SUPERVISOR = 'supervisor',
  LOGOUT = 'logout',
  LOCK_PROJECT = 'lock_project',
}

interface IWorkerStatusLoginModalProps {
  type?: WorkerStatusLoginModalType;
  onClose: () => void;
  onSuccess?: (type?: WorkerStatusLoginModalType) => void;
  autoFocus?: boolean;
  hideCloseIcon?: boolean;
  disableBackdropClose?: boolean;
}

const WorkerStatusLoginModal = ({
  type,
  onClose,
  onSuccess,
  autoFocus = true,
  hideCloseIcon = false,
  disableBackdropClose = false,
}: IWorkerStatusLoginModalProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [otp, setOtp] = useState('');
  const [hasError, setHasError] = useState(false);
  const isSupervisorMode = useAppSelector(state => state.app.isSupervisorMode);

  const dispatch = useAppDispatch();

  const onVerify = async () => {
    try {
      if (otp !== process.env.REACT_APP_WORKER_STATUS_LOGIN_PIN) {
        setHasError(true);
        return;
      }
      type === 'supervisor' && (await dispatch(setIsSupervisorMode(!isSupervisorMode)));
      onSuccess?.(type);
      onClose();
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  const onOtpChange = () => {
    if (hasError) {
      setHasError(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp) {
      onVerify();
    }
  };

  return (
    <Modal
      title={t('workerStatusLoginModal.title')}
      modalStyleClass={classes.modal}
      testId="worker-status-pin-modal"
      hideCloseIcon={hideCloseIcon}
      disableBackdropClose={disableBackdropClose}
      Content={
        <form autoComplete="off" onSubmit={e => e.preventDefault()}>
          <div onKeyDown={handleKeyDown}>
            <OtpInput
              allowCharacters={false}
              length={4}
              styleClass={classes.otpInput}
              testId="worker-status-pin-input"
              error={hasError ? t('workerStatusLoginModal.invalidPinError') : ''}
              onChange={onOtpChange}
              onComplete={otp => setOtp(otp)}
              onResetOtp={() => setOtp('')}
              autoFocus
            />
          </div>
        </form>
      }
      actionButtons={[
        {
          label: t('common.verify'),
          disabled: !otp,
          styleClass: classes.verifyButton,
          testId: 'worker-status-pin-verify-button',
          isLoading: false,
          onClick: onVerify,
        },
      ]}
      onClose={onClose}
    />
  );
};

export default memo(WorkerStatusLoginModal);
